/**
 * Smart-back referrer helpers.
 *
 * Every in-app navigation that lands on a page with a back button should
 * pass `state: { from: '<previous path + search>' }`. `useSmartBack` then
 * prefers that tracked referrer over the heuristic `document.referrer` /
 * browser-history fallback, so back buttons return to the actual previous
 * surface (e.g. Storefront → Settings → back goes to Storefront, not the
 * hardcoded `/inventory` default).
 *
 * Use:
 *   const navWithFrom = useNavigateWithFrom();
 *   navWithFrom('/settings');
 *
 *   <LinkWithFrom to="/u/me">Preview store</LinkWithFrom>
 *
 *   <PrefetchLink to={href} state={useFromState()} ... />
 */
import { useCallback, useMemo } from 'react';
import {
  Link,
  type LinkProps,
  type NavigateOptions,
  useLocation,
  useNavigate,
} from 'react-router-dom';

export type FromState = { from: string };

/** Current `location.pathname + search` as a `{ from }` state object. */
export function useFromState(): FromState {
  const location = useLocation();
  return useMemo(
    () => ({ from: location.pathname + location.search }),
    [location.pathname, location.search],
  );
}

/** `navigate(to, opts)` with `from` merged into `opts.state`. */
export function useNavigateWithFrom() {
  const navigate = useNavigate();
  const from = useFromState();
  return useCallback(
    (to: string, opts?: NavigateOptions) => {
      navigate(to, {
        ...opts,
        state: { ...(opts?.state as object | undefined), ...from },
      });
    },
    [navigate, from],
  );
}

/** Drop-in `<Link>` that auto-attaches `state.from`. */
export function LinkWithFrom({ state, ...rest }: LinkProps) {
  const from = useFromState();
  return <Link {...rest} state={{ ...(state as object | undefined), ...from }} />;
}
